import { FORMAT_LABELS, type ShareFormat } from '@core/types/share'
import { formatExpiry } from '@core/utils/format'
import { getShareExtension, buildFullShareUrl, buildEmbedCode, buildRawUrl } from '@/lib/utils/share-url'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  VStack,
  HStack,
  Icon,
  Flex,
  Box,
  useColorMode,
} from '@chakra-ui/react'
import { Check, Copy, Link, FileText, Code, FileCode } from 'lucide-react'
import { useCopyToClipboard } from '@/lib/hooks/useCopyToClipboard'

interface ShareCreatedModalProps {
  isOpen: boolean
  onClose: () => void
  shareId: string
  passcode: string | null
  expiresAt: number
  isPublic?: boolean
  filename?: string
  format?: ShareFormat
  embedAllowed?: boolean  // Pro tier only
}

export function ShareCreatedModal({
  isOpen,
  onClose,
  shareId,
  passcode,
  expiresAt,
  isPublic,
  filename,
  format = 'md',
  embedAllowed = false,
}: ShareCreatedModalProps) {
  const { colorMode } = useColorMode()
  const { copy, hasCopied } = useCopyToClipboard()

  const extension = getShareExtension(filename, format)
  const shareUrl = buildFullShareUrl(shareId, extension, passcode)
  const embedCode = buildEmbedCode(shareId, extension, passcode)
  const rawUrl = buildRawUrl(shareId, extension, passcode, isPublic)

  const copyToClipboard = (text: string, type: 'share' | 'embed' | 'raw' = 'share') => {
    copy(text, type, 'Copied to clipboard')
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent bg={colorMode === 'dark' ? 'stone.800' : 'white'}>
        <ModalHeader>
          <VStack spacing={2}>
            <Flex
              w={12}
              h={12}
              bg="green.100"
              borderRadius="full"
              align="center"
              justify="center"
            >
              <Icon as={Check} boxSize={6} color="green.600" />
            </Flex>
            <Text>Share Created!</Text>
            <Text fontSize="sm" fontWeight="normal" color="stone.500">
              Your secure Markdown share is ready
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={5} align="stretch">
            {/* Share Link */}
            <Box>
              <HStack mb={2}>
                <Icon as={Link} boxSize={4} color="stone.500" />
                <Text fontWeight="medium" fontSize="sm">Share Link</Text>
              </HStack>
              <Text fontSize="xs" color="stone.500" mb={2}>
                {isPublic
                  ? 'Public share - no passcode needed'
                  : passcode
                  ? 'Passcode is embedded for one-click access'
                  : 'Share the passcode separately for added security'}
              </Text>
              <InputGroup size="sm">
                <Input
                  value={shareUrl}
                  readOnly
                  fontFamily="mono"
                  fontSize="xs"
                  bg={colorMode === 'dark' ? 'stone.700' : 'stone.50'}
                  pr={10}
                />
                <InputRightElement>
                  <IconButton
                    aria-label="Copy share link"
                    icon={hasCopied('share') ? <Check size={14} color="green" /> : <Copy size={14} />}
                    variant="ghost"
                    size="xs"
                    onClick={() => copyToClipboard(shareUrl, 'share')}
                  />
                </InputRightElement>
              </InputGroup>
              <HStack spacing={4} mt={2} flexWrap="wrap">
                {filename && (
                  <HStack fontSize="xs" color="stone.500">
                    <Icon as={FileText} boxSize={3} />
                    <Text>{filename}</Text>
                  </HStack>
                )}
                <Text fontSize="xs" color="stone.500">
                  Format: {FORMAT_LABELS[format]}
                </Text>
                <Text fontSize="xs" color="stone.500">
                  Expires: {formatExpiry(expiresAt)}
                </Text>
              </HStack>
            </Box>

            {/* Embed Code - Pro tier only */}
            {embedAllowed && (
              <Box>
                <HStack mb={2}>
                  <Icon as={Code} boxSize={4} color="stone.500" />
                  <Text fontWeight="medium" fontSize="sm">Embed Code</Text>
                </HStack>
                <Text fontSize="xs" color="stone.500" mb={2}>
                  Add ?theme=dark or ?theme=light to the URL to set the theme
                </Text>
                <InputGroup size="sm">
                  <Input
                    value={embedCode}
                    readOnly
                    fontFamily="mono"
                    fontSize="xs"
                    bg={colorMode === 'dark' ? 'stone.700' : 'stone.50'}
                    pr={10}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label="Copy embed code"
                      icon={hasCopied('embed') ? <Check size={14} color="green" /> : <Copy size={14} />}
                      variant="ghost"
                      size="xs"
                      onClick={() => copyToClipboard(embedCode, 'embed')}
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>
            )}

            {/* Raw Link */}
            <Box>
              <HStack mb={2}>
                <Icon as={FileCode} boxSize={4} color="stone.500" />
                <Text fontWeight="medium" fontSize="sm">Raw Link</Text>
              </HStack>
              <Text fontSize="xs" color="stone.500" mb={2}>
                {isPublic
                  ? 'Direct access to raw markdown - works with curl, wget, and other tools'
                  : 'Browser only - the # fragment is not sent to the server, so external tools won\'t receive the passcode'}
              </Text>
              <InputGroup size="sm">
                <Input
                  value={rawUrl}
                  readOnly
                  fontFamily="mono"
                  fontSize="xs"
                  bg={colorMode === 'dark' ? 'stone.700' : 'stone.50'}
                  pr={10}
                />
                <InputRightElement>
                  <IconButton
                    aria-label="Copy raw link"
                    icon={hasCopied('raw') ? <Check size={14} color="green" /> : <Copy size={14} />}
                    variant="ghost"
                    size="xs"
                    onClick={() => copyToClipboard(rawUrl, 'raw')}
                  />
                </InputRightElement>
              </InputGroup>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter />
      </ModalContent>
    </Modal>
  )
}
